// Axios
import defaultAxios, { AxiosRequestConfig } from 'axios';

// Interfaces
import { IBaseApiResponse } from 'interfaces/api';

// Utils
import { api_getFailedResponse, api_getSuccessResponse } from 'utils';
import { messageUtils_error } from 'utils/common/message.utils';
import { oidc_userManager } from 'utils';

// Constant
import { ApiConstant, ApiConstantHc, ApiConstantHcBase } from 'constant';
import { LOCAL_STORAGE_ACCESS_TOKEN } from 'constant/local-storage.constant';
import { AUTHORIZATION } from 'constant/request-header.constant';

/**
 * @description Handle response from server
 *
 * @param {object} res
 *
 * @return {object} object
 */
const handleResponse = (res: any): IBaseApiResponse<{ res: any }> => {
  // Check if client get error request from server
  if (res && res?.response?.status >= 400) {
    const { data, status, config } = res.response;
    const response = api_getFailedResponse(status, { ok: false, res: data, config, message: data?.message || res?.message });

    /** @note logging for development only */
    if (['development', 'local'].includes(process.env.REACT_APP_ENV!)) console.error(response, 'AXIOS ERROR');

    return response;
  } else if (
    // Otherwise return the response according to server
    res &&
    res?.status >= 200 &&
    res?.status < 300
  ) {
    const { data, status, config, headers } = res;
    const ok =
      config?.responseType === 'blob' ||
      data?.success?.toString() === '1' ||
      String(data?.success) === '1' ||
      data?.success === true;

    const response = api_getSuccessResponse(status, {
      ok,
      res: data,
      config,
      message: data?.message || res?.statusText,
      headers,
    });

    /** @note logging for development only */
    const message = ok ? 'AXIOS OK' : 'AXIOS OK, BUT FAILED';
    if (['development', 'local'].includes(process.env.REACT_APP_ENV!)) {
      if (ok) {
        console.log(response, message);
      } else {
        console.error(response, message);
      }
    }

    return response;
  } else {
    // Check if there's no response, just return the plain object
    const { config } = res;
    const status = res.message === 'Network Error' ? 'NETWORKERROR' : res.code;
    const response = api_getFailedResponse(status, {
      ok: false,
      res,
      config,
      message: res?.message || res?.code,
    });

    /** @note logging for development only */
    if (['development', 'local'].includes(process.env.REACT_APP_ENV!)) console.error(response, 'AXIOS FAILED');

    return response;
  }
};

/**
 * @description Render axios url
 *
 * @param {ApiConstant} url
 * @param {ApiConstant.WEBPAGE|ApiConstant.BACK_OFFICE} type
 *
 * @return {string} string
 */
const axios_renderUrl = (url: ApiConstant, type: ApiConstant.WEBPAGE | ApiConstant.BACK_OFFICE): string => {
  switch (type) {
    case ApiConstant.BACK_OFFICE:
      return `${process.env.REACT_APP_BASE_URL}/${ApiConstant.BACK_OFFICE_URL}/${url}`;
    case ApiConstant.WEBPAGE:
      return `${process.env.REACT_APP_BASE_URL}/${ApiConstant.WEBPAGE_URL}/${url}`;
    default:
      return '';
  }
};

/**
 * @description Render axios hc url
 *
 * @param {ApiConstantHc} url
 * @param {ApiConstantHcBase} type
 *
 * @return {string} string
 */
const axios_renderHcUrl = (url: ApiConstantHc, type: ApiConstantHcBase): string => {
  const { ASSESSMENT, MASTER } = ApiConstantHcBase;

  switch (type) {
    case ASSESSMENT:
      return `${process.env.REACT_APP_BASE_URL_HC}/${ASSESSMENT}/${url}`;
    case MASTER:
      return `${process.env.REACT_APP_BASE_URL_HC}/${MASTER}/${url}`;
    default:
      return '';
  }
};

const config: AxiosRequestConfig = {
  baseURL: 'http://localhost', // look axios_renderUrl & axios_renderHcUrl above for rendering BASE_URL
  timeout: 30000,
};

const axios = defaultAxios.create(config);

axios.interceptors.request.use(
  async set => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN);

    // Check if user is authenticated, then assign headers for authorization
    if (accessToken && (!set.url?.includes('/lspbe_cms/api') || set.url?.includes('/certifications/'))) {
      set.headers![AUTHORIZATION] = `Bearer ${accessToken}`;
    }

    return set;
  },
  error => {
    return handleResponse(error);
  },
);

axios.interceptors.response.use(
  async response => {
    const res = handleResponse(response);

    // Success hit, but server throwing failed response from "success": "0" Throw the error alert
    if (!res.ok && res?.config?.responseType !== 'blob') {
      messageUtils_error(
        `${process.env.REACT_APP_ENV === 'development' ? `${res?.config?.url}:` : ''} ${
          res?.message || 'Hit Success, but failed when retrieve data from server'
        }`,
        {
          duration: 2,
        },
      );
    }

    return res;
  },
  async error => {
    if (defaultAxios?.isCancel(error)) {
      /** @note logging for development only */
      if (['development', 'local'].includes(process.env.REACT_APP_ENV!)) console.warn('CANCELED');

      return error;
    } else {
      const errorResponse = handleResponse(error);
      const originalRequest = error.config;

      // Check if theres any error when unauthorized
      if (errorResponse.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const { access_token: accessToken } = await oidc_userManager(true).signinSilent();

          // Set The Access Token To Axios Headers
          axios.defaults.headers.common[AUTHORIZATION] = accessToken;

          // Set To Local Storage
          localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, accessToken);

          return axios(originalRequest);
        } catch (err) {
          if (errorResponse?.status !== 'ECONNABORTED') localStorage.clear();
        }
      }

      // Throw the error alert
      messageUtils_error(
        `${['development', 'local'].includes(process.env.REACT_APP_ENV!) ? `${errorResponse?.config?.url}:` : ''} ${
          errorResponse.res?.title || errorResponse.res?.message || errorResponse?.message || 'Something When Wrong From Server'
        }`,
        {
          duration: 3,
        },
      );

      return errorResponse;
    }
  },
);

export { axios, axios_renderUrl, axios_renderHcUrl };
